/* eslint-disable max-lines */
/* eslint-disable react/no-multi-comp */
import type { ReactElement } from "react";
import { useCallback, useMemo, useRef } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { findMatchingMetricRange } from "@waterly/utils-isomorphic";

import type { MetricRangeCardMetricRange, MetricRangeFormikValues } from "../metricRangeUtils";
import { metricRangeCardMetricRandKeyGenerator, buildRangeExpression } from "../metricRangeUtils";

import RangeBarIcon from "./RangeBarIcon";

export type RangeBarMetricRange = Pick<
  MetricRangeCardMetricRange,
  | "id"
  | "name"
  | "rangeMin"
  | "rangeMax"
  | "rangeMinInclusive"
  | "rangeMaxInclusive"
  | "notificationEnabled"
  | "color"
  | "ideal"
>;

type PositionedSegment = {
  id: string;
  name: string;
  color: string;
  leftPercent: number;
  widthPercent: number;
  isCurrent: boolean;
  rangeMin: number | null;
  rangeMax: number | null;
  rangeMinInclusive: boolean;
  rangeMaxInclusive: boolean;
};

type OverlapZone = {
  key: string;
  leftPercent: number;
  widthPercent: number;
  // the current range's color used for the outline
  outlineColor: string;
};

// sub-slice of the current range that does NOT overlap any other range
type CurrentSlice = {
  key: string;
  leftPercent: number;
  widthPercent: number;
};

const formatRangeLabel = (segment: PositionedSegment): string => {
  const { name, rangeMin, rangeMax, rangeMinInclusive, rangeMaxInclusive } = segment;

  const prefix = name ? `${name}: ` : "";
  const rangeExpression = buildRangeExpression({ rangeMin, rangeMax, rangeMinInclusive, rangeMaxInclusive });

  return `${prefix}${rangeExpression}`;
};

type TooltipItem = { id: string; color: string; label: string };

const RangeTooltipContent = ({ items }: { items: TooltipItem[] }) => (
  <Stack spacing={0.5}>
    {items.map((item) => (
      <Stack key={item.id} direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "2px",
            backgroundColor: item.color,
            flexShrink: 0,
          }}
        />
        <span>{item.label}</span>
      </Stack>
    ))}
  </Stack>
);

const UNBOUNDED_PERCENT = 20;
const DEFAULT_BAR_HEIGHT = 20;
const DISPLAY_INDICATOR_HEIGHT_MULTIPLIER = 2.2;
const TRANSITION = "left 0.2s ease, width 0.2s ease";

type PositionScale = {
  globalMin: number;
  globalMax: number;
  totalSpan: number;
  leftPad: number;
  available: number;
};

const mergeCurrentIntoRanges = (
  allRanges: RangeBarMetricRange[],
  currentRange: MetricRangeFormikValues,
): RangeBarMetricRange[] => {
  const isExisting = currentRange.id !== undefined && allRanges.some((r) => r.id === currentRange.id);
  if (isExisting) {
    return allRanges.map((range) =>
      range.id === currentRange.id ? ({ ...range, ...currentRange } as RangeBarMetricRange) : range,
    );
  }
  return [...allRanges, currentRange as RangeBarMetricRange];
};

const toPercent = (
  val: number,
  globalMin: number,
  totalSpan: number,
  leftPad: number,
  available: number,
): number => {
  if (totalSpan === 0) return leftPad;
  return leftPad + ((val - globalMin) / totalSpan) * available;
};

const clampPercent = (value: number): number => Math.max(0, Math.min(100, value));

const isSingletonSegment = (segment: PositionedSegment): boolean =>
  segment.rangeMin !== null && segment.rangeMin === segment.rangeMax;

const getPositionScale = (ranges: RangeBarMetricRange[]): PositionScale | null => {
  const finiteValues = ranges
    .flatMap((r) => [r.rangeMin, r.rangeMax])
    .filter((v): v is number => v !== null && v !== undefined);

  if (finiteValues.length === 0) return null;

  const globalMin = Math.min(...finiteValues);
  const globalMax = Math.max(...finiteValues);

  return {
    globalMin,
    globalMax,
    totalSpan: globalMax - globalMin,
    leftPad: UNBOUNDED_PERCENT,
    available: 100 - UNBOUNDED_PERCENT - UNBOUNDED_PERCENT,
  };
};

const getDisplayValueIndicatorPercent = (
  value: number | null,
  ranges: RangeBarMetricRange[],
  segments: PositionedSegment[],
  matchedRangeId: string | null,
): number | null => {
  if (value === null) return null;

  const matchedSegment = matchedRangeId ? segments.find((segment) => segment.id === matchedRangeId) : null;

  if (matchedSegment && (matchedSegment.rangeMin === null || matchedSegment.rangeMax === null)) {
    return clampPercent(matchedSegment.leftPercent + matchedSegment.widthPercent / 2);
  }

  const scale = getPositionScale(ranges);
  if (!scale) return null;

  const { globalMin, totalSpan, leftPad, available } = scale;

  return clampPercent(toPercent(value, globalMin, totalSpan, leftPad, available));
};

const positionRanges = (ranges: RangeBarMetricRange[]): PositionedSegment[] => {
  if (ranges.length === 0) return [];

  const scale = getPositionScale(ranges);

  if (!scale) {
    const w = 100 / ranges.length;
    return ranges.map((range, i) => ({
      id: metricRangeCardMetricRandKeyGenerator(range),
      name: range.name,
      color: range.color,
      leftPercent: i * w,
      widthPercent: w,
      isCurrent: false,
      rangeMin: range.rangeMin ?? null,
      rangeMax: range.rangeMax ?? null,
      rangeMinInclusive: range.rangeMinInclusive,
      rangeMaxInclusive: range.rangeMaxInclusive,
    }));
  }

  const { globalMin, totalSpan, leftPad, available } = scale;

  return ranges.map((range) => {
    const rangeMin = range.rangeMin ?? null;
    const rangeMax = range.rangeMax ?? null;
    const leftEdge = rangeMin === null ? 0 : toPercent(rangeMin, globalMin, totalSpan, leftPad, available);
    const rightEdge = rangeMax === null ? 100 : toPercent(rangeMax, globalMin, totalSpan, leftPad, available);
    const width = Math.max(rightEdge - leftEdge, 1);

    return {
      id: metricRangeCardMetricRandKeyGenerator(range),
      name: range.name,
      color: range.color,
      leftPercent: leftEdge,
      widthPercent: width,
      isCurrent: false,
      rangeMin,
      rangeMax,
      rangeMinInclusive: range.rangeMinInclusive,
      rangeMaxInclusive: range.rangeMaxInclusive,
    };
  });
};

const buildPositionedSegments = (
  allRanges: RangeBarMetricRange[],
  currentRange: MetricRangeFormikValues,
): PositionedSegment[] => {
  const merged = mergeCurrentIntoRanges(allRanges, currentRange);
  const segments = positionRanges(merged);
  return segments.map((s) => ({
    ...s,
    isCurrent: s.id === currentRange.id,
  }));
};

const buildOverlapZones = (segments: PositionedSegment[]): OverlapZone[] => {
  const current = segments.find((s) => s.isCurrent);
  if (!current) return [];

  const currentLeft = current.leftPercent;
  const currentRight = current.leftPercent + current.widthPercent;

  return segments
    .filter((s) => !s.isCurrent)
    .map((other) => {
      const overlapLeft = Math.max(currentLeft, other.leftPercent);
      const overlapRight = Math.min(currentRight, other.leftPercent + other.widthPercent);
      if (overlapRight <= overlapLeft) return null;

      return {
        key: `overlap-${other.id}`,
        leftPercent: overlapLeft,
        widthPercent: overlapRight - overlapLeft,
        outlineColor: current.color,
      };
    })
    .filter((z): z is OverlapZone => z !== null);
};

// split the current range into slices that exclude overlap zones
const buildCurrentSlices = (segments: PositionedSegment[], overlaps: OverlapZone[]): CurrentSlice[] => {
  const current = segments.find((s) => s.isCurrent);
  if (!current) return [];

  if (overlaps.length === 0) {
    return [{ key: "current-full", leftPercent: current.leftPercent, widthPercent: current.widthPercent }];
  }

  const currentLeft = current.leftPercent;
  const currentRight = current.leftPercent + current.widthPercent;
  const sortedOverlaps = [...overlaps].sort((a, b) => a.leftPercent - b.leftPercent);

  const slices: CurrentSlice[] = [];
  let cursor = currentLeft;

  for (const overlap of sortedOverlaps) {
    if (overlap.leftPercent > cursor) {
      slices.push({
        key: `slice-${cursor}`,
        leftPercent: cursor,
        widthPercent: overlap.leftPercent - cursor,
      });
    }
    cursor = Math.max(cursor, overlap.leftPercent + overlap.widthPercent);
  }

  if (cursor < currentRight) {
    slices.push({
      key: `slice-${cursor}`,
      leftPercent: cursor,
      widthPercent: currentRight - cursor,
    });
  }

  return slices;
};

type GapZone = Omit<PositionedSegment, "id" | "name" | "color" | "isCurrent"> & {
  key: string;
};

// find grey areas not covered by any segment
const buildGapZones = (segments: PositionedSegment[]): GapZone[] => {
  const sortedSegments = [...segments].sort((a, b) => a.leftPercent - b.leftPercent);

  // merge overlapping intervals, tracking boundary values
  type Interval = {
    left: number;
    right: number;
    rangeMin: number | null;
    rangeMax: number | null;
    rangeMinInclusive: boolean;
    rangeMaxInclusive: boolean;
  };
  const mergedRanges: Interval[] = [];

  for (let i = 0; i < sortedSegments.length; i++) {
    const segment = sortedSegments[i];

    const segmentRightPercent = segment.leftPercent + segment.widthPercent;
    const latestMergedRange = mergedRanges[mergedRanges.length - 1];
    if (latestMergedRange && segment.leftPercent <= latestMergedRange.right) {
      if (segmentRightPercent > latestMergedRange.right) {
        latestMergedRange.right = segmentRightPercent;
        latestMergedRange.rangeMax = segment.rangeMax;
        latestMergedRange.rangeMaxInclusive = segment.rangeMaxInclusive;
      }
    } else {
      mergedRanges.push({
        left: segment.leftPercent,
        right: segmentRightPercent,
        rangeMin: segment.rangeMin,
        rangeMax: segment.rangeMax,
        rangeMinInclusive: segment.rangeMinInclusive,
        rangeMaxInclusive: segment.rangeMaxInclusive,
      });
    }
  }

  const gaps: GapZone[] = [];
  let cursor = 0;
  let cursorValue: number | null = null;

  for (let i = 0; i < mergedRanges.length; i++) {
    const previousRange = mergedRanges[i - 1];
    const range = mergedRanges[i];

    if (range.left > cursor + 0.1) {
      gaps.push({
        key: `gap-${cursor}`,
        leftPercent: cursor,
        widthPercent: range.left - cursor,
        rangeMin: cursorValue,
        rangeMax: range.rangeMin,
        rangeMinInclusive: i === 0 ? false : !previousRange.rangeMaxInclusive,
        rangeMaxInclusive: !range.rangeMinInclusive,
      });
    }

    cursor = range.right;
    cursorValue = range.rangeMax;
  }

  if (cursor < 99.9) {
    gaps.push({
      key: `gap-${cursor}`,
      leftPercent: cursor,
      widthPercent: 100 - cursor,
      rangeMin: cursorValue,
      rangeMax: null,
      rangeMinInclusive: !mergedRanges[mergedRanges.length - 1].rangeMaxInclusive,
      rangeMaxInclusive: false,
    });
  }

  return gaps;
};

const getDisplayIndicatorHeight = (height: number): number =>
  Math.round(height * DISPLAY_INDICATOR_HEIGHT_MULTIPLIER);

const getDisplayWrapperPaddingY = (height: number): string =>
  `${Math.max((getDisplayIndicatorHeight(height) - height) / 2, 8)}px`;

const getEmptyBarSx = (height: number) =>
  ({
    height,
    borderRadius: height / 2,
    backgroundColor: "action.disabledBackground",
    width: "100%",
  } as const);

const getBarContainerSx = (height: number) =>
  ({
    ...getEmptyBarSx(height),
    position: "relative",
    overflow: "hidden",
  } as const);

const GapZones = ({ zones }: { zones: GapZone[] }) => {
  const { t } = useTranslation(["common"]);

  const formatGapLabel = useCallback(
    (gap: GapZone): string => {
      const { rangeMin, rangeMax, rangeMinInclusive, rangeMaxInclusive } = gap;

      const rangeExpression = buildRangeExpression({
        rangeMin,
        rangeMax,
        rangeMinInclusive,
        rangeMaxInclusive,
      });

      return `${t("common:no_range")} ${rangeExpression}`;
    },
    [t],
  );
  return (
    <>
      {zones.map((gap) => (
        <Tooltip key={gap.key} title={formatGapLabel(gap)} arrow placement="top">
          <Box
            sx={{
              position: "absolute",
              left: `${gap.leftPercent}%`,
              width: `${gap.widthPercent}%`,
              height: "100%",
            }}
          />
        </Tooltip>
      ))}
    </>
  );
};

const SegmentTooltip = ({ segment, children }: { segment: PositionedSegment; children: ReactElement }) => (
  <Tooltip
    title={
      <RangeTooltipContent
        items={[{ id: segment.id, color: segment.color, label: formatRangeLabel(segment) }]}
      />
    }
    arrow
    placement="top"
  >
    {children}
  </Tooltip>
);

export enum RangeBarMode {
  EDIT = "EDIT",
  DISPLAY = "DISPLAY",
}

type RangeBarDisplayProps = {
  mode: RangeBarMode.DISPLAY;
  allRanges: RangeBarMetricRange[];
  value: number | null;
  height?: number;
};

const RangeBarDisplay = ({
  allRanges,
  value,
  height = DEFAULT_BAR_HEIGHT,
}: Omit<RangeBarDisplayProps, "mode">) => {
  const segments = useMemo(() => positionRanges(allRanges), [allRanges]);
  const gapZones = useMemo(() => buildGapZones(segments), [segments]);
  const indicatorHeight = getDisplayIndicatorHeight(height);
  const metricRange = useMemo(() => {
    const normalized = allRanges.map((r) => ({
      ...r,
      rangeMin: r.rangeMin ?? null,
      rangeMax: r.rangeMax ?? null,
    }));
    const { metricRange } = findMatchingMetricRange(value, normalized);
    return metricRange;
  }, [value, allRanges]);
  const matchedRangeId = metricRange?.id ?? null;
  const valueIndicatorPercent = useMemo(
    () => getDisplayValueIndicatorPercent(value, allRanges, segments, matchedRangeId),
    [allRanges, matchedRangeId, segments, value],
  );

  if (segments.length === 0) return <Box sx={getEmptyBarSx(height)} />;

  return (
    <Box sx={{ position: "relative", width: "100%", py: getDisplayWrapperPaddingY(height) }}>
      <Box data-testid="range-bar-container" sx={getBarContainerSx(height)}>
        <GapZones zones={gapZones} />
        {segments.map((segment) => (
          <SegmentTooltip key={segment.id} segment={segment}>
            <Box
              data-testid={`range-bar-segment-${segment.id}`}
              sx={{
                position: "absolute",
                left: `${segment.leftPercent}%`,
                width: `${segment.widthPercent}%`,
                height: "100%",
                backgroundColor: segment.color,
                opacity: segment.id === matchedRangeId ? 1 : 0.2,
                zIndex: isSingletonSegment(segment) ? 1 : undefined,
                transition: TRANSITION,
              }}
            />
          </SegmentTooltip>
        ))}
      </Box>
      {valueIndicatorPercent !== null && (
        <Box
          data-testid="range-bar-value-indicator"
          sx={{
            position: "absolute",
            left: `clamp(${indicatorHeight / 2}px, ${valueIndicatorPercent}%, calc(100% - ${
              indicatorHeight / 2
            }px))`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <RangeBarIcon
            data-testid="range-bar-value-indicator-icon"
            outerColor="black"
            innerColor={metricRange?.color}
            sx={{
              color: "#000",
              fontSize: indicatorHeight,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

type RangeBarEditProps = {
  mode: RangeBarMode.EDIT;
  allRanges: RangeBarMetricRange[];
  currentRange: MetricRangeFormikValues;
  height?: number;
};

const RangeBarEdit = ({
  allRanges,
  currentRange,
  height = DEFAULT_BAR_HEIGHT,
}: Omit<RangeBarEditProps, "mode">) => {
  const tempIdRef = useRef(uuid());
  const isNewRange = currentRange.id === undefined;

  const stableCurrentRange = useMemo<MetricRangeFormikValues>(() => {
    if (!isNewRange) return currentRange;
    return { ...currentRange, id: tempIdRef.current };
  }, [currentRange, isNewRange]);

  const segments = useMemo(
    () => buildPositionedSegments(allRanges, stableCurrentRange),
    [allRanges, stableCurrentRange],
  );

  const current = segments.find((s) => s.isCurrent);
  const gapZones = useMemo(() => buildGapZones(segments), [segments]);
  const overlapZones = useMemo(() => buildOverlapZones(segments), [segments]);
  const currentSlices = useMemo(() => buildCurrentSlices(segments, overlapZones), [segments, overlapZones]);

  const currentTooltipItems = useMemo<TooltipItem[]>(() => {
    if (!current) return [];
    const items: TooltipItem[] = [{ id: current.id, color: current.color, label: formatRangeLabel(current) }];
    const currentLeft = current.leftPercent;
    const currentRight = current.leftPercent + current.widthPercent;
    for (const s of segments) {
      if (s.isCurrent) continue;
      const otherRight = s.leftPercent + s.widthPercent;
      if (s.leftPercent < currentRight && otherRight > currentLeft) {
        items.push({ id: s.id, color: s.color, label: formatRangeLabel(s) });
      }
    }
    return items;
  }, [current, segments]);

  if (segments.length === 0) return <Box sx={getEmptyBarSx(height)} />;

  return (
    <Box sx={{ position: "relative", width: "100%", py: 1 }}>
      <Box data-testid="range-bar-container" sx={getBarContainerSx(height)}>
        <GapZones zones={gapZones} />
        {segments
          .filter((s) => !s.isCurrent)
          .map((segment) => (
            <SegmentTooltip key={segment.id} segment={segment}>
              <Box
                data-testid={`range-bar-segment-${segment.id}`}
                sx={{
                  position: "absolute",
                  left: `${segment.leftPercent}%`,
                  width: `${segment.widthPercent}%`,
                  height: "100%",
                  backgroundColor: segment.color,
                  zIndex: isSingletonSegment(segment) ? 1 : undefined,
                  transition: TRANSITION,
                }}
              />
            </SegmentTooltip>
          ))}
        {current &&
          currentSlices.map((slice) => (
            <Box
              key={slice.key}
              sx={{
                position: "absolute",
                left: `${slice.leftPercent}%`,
                width: `${slice.widthPercent}%`,
                height: "100%",
                backgroundColor: current.color,
                zIndex: 1,
                transition: TRANSITION,
              }}
            />
          ))}
        {current && (
          <Tooltip title={<RangeTooltipContent items={currentTooltipItems} />} arrow placement="top">
            <Box
              sx={{
                position: "absolute",
                left: `${current.leftPercent}%`,
                width: `${current.widthPercent}%`,
                height: "100%",
                boxSizing: "border-box",
                border: `2px solid ${current.color}`,
                zIndex: 2,
                transition: TRANSITION,
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

type RangeBarProps = RangeBarEditProps | RangeBarDisplayProps;

const RangeBar = (props: RangeBarProps) => {
  // eslint-disable-next-line react/destructuring-assignment
  if (props.mode === RangeBarMode.DISPLAY) {
    const { allRanges, value, height } = props;
    return <RangeBarDisplay allRanges={allRanges} value={value} height={height} />;
  }

  const { allRanges, currentRange, height } = props;
  return <RangeBarEdit allRanges={allRanges} currentRange={currentRange} height={height} />;
};

export default RangeBar;
